import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { clone } from "@formant/core/common/clone";
import { maxThrottleHz } from "@formant/core/common/maxThrottleHz";
import { narrowError } from "@formant/core/common/narrowError";
import { normalThrottleHzThreshold } from "@formant/core/common/normalThrottleHzThreshold";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceStreamConfiguration } from "@formant/core/model/IDeviceStreamConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { videoQualities } from "@formant/core/model/videoQualities";
import { VideoQuality } from "@formant/core/model/VideoQuality";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import { deviceClient } from "../../clients";
import { appStore, authStore } from "../../stores";
import { Accordion } from "../common/Accordion";
import { Buttons } from "../common/Buttons";
import { Heading2 } from "../common/Heading2";
import { Help } from "../common/Help";
import { NumberField } from "../common/NumberField";
import { Popup } from "../common/Popup";
import { SelectField } from "../common/SelectField";
import { StandardButton } from "../common/StandardButton";
import { Switch } from "../common/Switch";
import { ValidationMessages } from "../common/ValidationMessages";
import { minQualityForStreams } from "../Observe/utils/minQualityForStreams";
import * as styles from "./EditStream.scss";
import { EditStreamDetails } from "./EditStreamDetails";
import { EditStreamHeader } from "./EditStreamHeader";
import { typeToString } from "./typeToString";

export interface IEditStreamProps {
    devices?: IDevice[];
    document: IDeviceConfigurationDocument;
    streams: IDeviceStreamConfiguration[];
    editing: boolean;
    visible: boolean;
    onClose: () => void;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onAdd: (
        stream: IDeviceStreamConfiguration,
        replacesStream?: IDeviceStreamConfiguration,
    ) => void;
}

@observer
export class EditStream extends Component<IEditStreamProps> {
    @observable
    private streams: IDeviceStreamConfiguration[] = clone(this.props.streams);
    @observable
    private validation: IValidation = {};

    private validatedStream?: IDeviceStreamConfiguration;

    private originalStream: IDeviceStreamConfiguration | null = null;

    public componentDidMount() {
        const videoEncodingEnabled = this.streams.some(
            _ => _.transform?.videoEncodingEnabled,
        );
        const quality = minQualityForStreams(this.streams);
        if (videoEncodingEnabled) {
            this.streams.forEach(stream => (stream.quality = quality));
        }

        if (
            this.streams.length === 1 &&
            this.streams[0].name !== "" &&
            this.streams[0].configuration.type === "ros-localization"
        ) {
            this.originalStream = clone(this.streams[0]);
        }
    }
    public render() {
        const { validation } = this;
        const { editing, devices, document, visible, onChange } = this.props;

        const streams = this.streams.map((stream, index) => (
            <div key={`${stream.name}.${index}`}>
                <div className={styles["stream-divider"]} />
                <EditStreamDetails
                    devices={devices}
                    document={document}
                    stream={stream}
                    validation={
                        stream === this.validatedStream ? validation : {}
                    }
                    onChange={onChange}
                    onDelete={
                        this.streams.length > 1 ? this.onDelete : undefined
                    }
                />
            </div>
        ));

        const firstStream =
            this.streams.length > 0 ? this.streams[0] : undefined;
        const isOnDemand = this.streams.some(_ => _.onDemand);
        const videoEncodingEnabled = this.streams.some(
            _ => _.transform?.videoEncodingEnabled,
        );
        const showValidationFields = this.streams.some(
            _ => _.validation !== null && _.validation !== undefined,
        );
        const quality = minQualityForStreams(this.streams);

        return (
            <Popup
                visible={visible}
                windowClassName={styles["edit-stream"]}
                onClose={this.onClose}
                variant="light"
            >
                <div className={styles.header}>
                    {firstStream && (
                        <Heading2 className={styles.heading}>
                            {`${editing ? "Edit" : "New"} ${typeToString(
                                firstStream.configuration.type,
                            )} Stream`}
                        </Heading2>
                    )}
                </div>
                {firstStream && (
                    <EditStreamHeader stream={firstStream} onAdd={this.onAdd} />
                )}
                <div className={styles["field-row"]}>
                    <TextField
                        className={styles.field}
                        label="Stream name"
                        placeholder="Telemetry stream name"
                        small={true}
                        value={firstStream?.name}
                        invalid={!!validation?.name}
                        onChange={this.onChangeName}
                        data-testid="stream-name-input"
                    />
                    <Help
                        className={styles.help}
                        text="Stream name must be dot-separated (.) alphanumeric parts with characters a-z, A-Z, 0-9, dash (-), and underscore (_)"
                    />
                </div>
                <>
                    {authStore.featureEnabled("onDemand") && (
                        <Switch
                            state={isOnDemand}
                            label="On demand"
                            onChange={this.onChangeUploadStream}
                            stateLabels={["Off", "On"]}
                            className={styles["on-demand"]}
                            data-testid="on-demand-switch"
                        />
                    )}
                    <Help
                        text="On-demand streams will only ingest when requested by the user. Click for docs."
                        to="https://docs.formant.io/docs/on-demand-streams"
                    />
                </>
                <Switch
                    disabled={firstStream?.configuration.type === "hardware"}
                    state={videoEncodingEnabled}
                    label="Video encoding"
                    onChange={this.onChangeEncodeVideo}
                    info="Only applicable for image streams. Choose whether you want images ingested on this stream to be encoded as a video."
                    stateLabels={["Off", "On"]}
                    className={styles["encode-video"]}
                />
                <>
                    <Switch
                        disabled={
                            firstStream?.configuration.type === "hardware"
                        }
                        state={showValidationFields}
                        label="Validation schema"
                        onChange={this.onChangeValidation}
                        info="Only applicable for JSON and text streams. Choose a schema configuration for this stream."
                        stateLabels={["Off", "On"]}
                        className={styles["validation-schema"]}
                    />
                </>
                {showValidationFields && (
                    <div className={styles["field-row"]}>
                        <TextField
                            className={styles.field}
                            label="Schema id"
                            placeholder="(e.g. geojson, formant/geometry)"
                            small={true}
                            value={firstStream?.validation?.schemaId}
                            onChange={this.onChangeSchemaId}
                            invalid={!!validation["validation.schemaId"]}
                        />
                    </div>
                )}
                {videoEncodingEnabled && (
                    <>
                        <p>
                            Video quality
                            <Help text="If this video source is also configured for teleop, the lower of the two qualities will be used." />
                        </p>
                        <SelectField
                            label="Preferred video quality"
                            value={quality}
                            onChange={this.onChangeQuality}
                            small={true}
                        >
                            {videoQualities.map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                        </SelectField>
                    </>
                )}
                {streams}
                <>
                    <Accordion
                        visible={
                            !videoEncodingEnabled ||
                            !!this.validation?.throttleHz
                        }
                        maxHeight="9.375rem"
                    >
                        <div className={styles["stream-divider"]} />
                        <p>
                            Optional rate limit.
                            <Help
                                text="Device will not ingest faster than the rate limit. Click for docs."
                                to="https://docs.formant.io/docs/configure-stream-rate"
                            />
                        </p>
                        <NumberField
                            className={styles.field}
                            label="Rate limit (Hz)"
                            small={true}
                            numberValue={firstStream?.throttleHz ?? undefined}
                            invalid={!!validation?.throttleHz}
                            onChangeNumber={this.onChangeThrottleRate}
                        />
                    </Accordion>
                </>
                <ValidationMessages validation={this.validation} />
                <Buttons className={styles.buttons}>
                    <StandardButton
                        primary={true}
                        onClicked={this.onDone}
                        data-testid="done-button"
                    >
                        Done
                    </StandardButton>
                </Buttons>
            </Popup>
        );
    }

    private onChangeName = (event: ChangeEvent<InputElement>) => {
        this.streams.forEach(_ => (_.name = event.target.value));
    };

    private onChangeThrottleRate = (value?: number) => {
        this.streams.forEach(_ => (_.throttleHz = value));
    };

    private onChangeUploadStream = (uploadStream: boolean) => {
        this.streams.forEach(_ => (_.onDemand = uploadStream));
    };

    private onChangeValidation = (useValidation: boolean) => {
        this.streams.forEach(
            _ => (_.validation = useValidation ? { schemaId: "" } : null),
        );
    };

    private onChangeSchemaId = (event: ChangeEvent<InputElement>) => {
        const schemaId = event.target.value;
        this.streams.forEach(_ => {
            _.validation = { ...(_.validation || {}), schemaId };
        });
    };

    private onChangeQuality = (event: ChangeEvent<HTMLSelectElement>) => {
        const quality = event.target.value as VideoQuality;
        this.streams.forEach(_ => (_.quality = quality));
    };

    private onChangeEncodeVideo = (encodeVideo: boolean) => {
        this.streams.forEach(_ => {
            _.transform = {
                ..._.transform,
                videoEncodingEnabled: encodeVideo,
            };
        });
    };

    private onAdd = (stream: IDeviceStreamConfiguration) => {
        this.streams.push(stream);
    };

    private onClose = () => {
        this.props.onClose();
    };

    private onDelete = (stream: IDeviceStreamConfiguration) => {
        this.streams = this.streams.filter(_ => _ !== stream);
    };

    private onDone = async () => {
        const confirmDone = async () => {
            const { document, onChange } = this.props;
            const originalStreams = new Set(this.props.streams);

            for (const stream of this.streams) {
                const existing = (document.telemetry?.streams || []).some(
                    _ =>
                        _.name === stream.name &&
                        !_.disabled &&
                        !originalStreams.has(_),
                );
                if (existing) {
                    this.validation = {
                        name: ["A stream with this name already exists"],
                    };
                    return;
                }

                try {
                    await deviceClient.validateStreamConfiguration(
                        await authStore.token(),
                        stream,
                    );
                } catch (error) {
                    const responseError = narrowError(
                        error,
                        ResponseError,
                        _ => _.statusCode === 422,
                    );
                    this.validatedStream = stream;
                    this.validation = responseError.validationErrors;

                    return;
                }
            }
            // remove original streams
            const streams = (document.telemetry?.streams || []).filter(
                _ => !originalStreams.has(_),
            );
            onChange({
                ...document,
                telemetry: {
                    ...document.telemetry,
                    streams,
                },
            });
            // add all streams
            if (this.originalStream) {
                // in case the stream name has changed, we provide the original to replace
                this.props.onAdd(this.streams[0], this.originalStream);
            } else {
                this.streams.forEach(_ => this.props.onAdd(_));
            }
        };
        const throttleExceedsNormal = this.streams.reduce(
            (acc, { throttleHz }) =>
                acc ||
                (!!throttleHz &&
                    throttleHz > normalThrottleHzThreshold &&
                    throttleHz < maxThrottleHz),
            false,
        );
        if (throttleExceedsNormal) {
            appStore.setConfirm({
                title: `Setting stream rate to greater than ${normalThrottleHzThreshold}Hz?`,
                message: (
                    <>
                        <p>
                            Only JSON streams can be ingested at a rate higher
                            than {normalThrottleHzThreshold}Hz. Setting any
                            other stream type to a rate higher than{" "}
                            {normalThrottleHzThreshold}Hz will limit the rate
                            down to {normalThrottleHzThreshold}
                            Hz.
                        </p>
                        <p>
                            Ingesting at high frequency can impact device
                            performance and network throughput. Are you sure you
                            want to ingest this stream at high frequency?
                        </p>
                    </>
                ),
                okCopy: "yes",
                cancelCopy: "no",
                onOk: confirmDone,
            });
        } else {
            await confirmDone();
        }
    };
}
